/* ==========================================================================
   HAYANOON — sale countdown
   ==========================================================================
   Two modes, set in the theme editor.

   duration  Starts at N minutes per visitor. The deadline is stored in
             sessionStorage, so it keeps counting down as the visitor moves
             between pages rather than snapping back to N:00 on every click —
             which is the tell that makes these timers stop working. It resets
             on their next visit.

   date      Counts to a real timestamp rendered by Liquid with the shop's
             UTC offset. Two visitors looking at the store at the same moment
             see the same number.

   Accessibility
   - The ticking digits are aria-hidden. A screen reader announcing a new
     number every second is unusable.
   - A polite live region carries a plain-language summary and is only
     rewritten when the minute changes, so it announces at a human pace.

   Performance
   - One setInterval at 1s, cleared when the tab is hidden and restarted on
     return. No rAF loop, no per-frame layout reads.
   ========================================================================== */

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
const STORE_KEY = 'hayanoon:countdown-ends';

class BrandCountdown extends HTMLElement {
  connectedCallback() {
    this.mode = this.dataset.mode === 'duration' ? 'duration' : 'date';
    this.restartMs = (parseFloat(this.dataset.restart) || 0) * 3600000;
    this.onExpiry = this.dataset.expiry === 'message' ? 'message' : 'hide';

    this.endAt = this.mode === 'duration'
      ? this.resolveDuration()
      : Date.parse(this.dataset.end || '');

    // A malformed or missing deadline must not leave a broken bar on the page.
    if (!this.endAt || Number.isNaN(this.endAt)) {
      this.remove();
      return;
    }

    this.fields = {
      days: this.querySelector('[data-ct-days]'),
      hours: this.querySelector('[data-ct-hours]'),
      minutes: this.querySelector('[data-ct-minutes]'),
      seconds: this.querySelector('[data-ct-seconds]'),
    };
    this.live = this.querySelector('[data-ct-live]');
    this.lastMinuteAnnounced = null;

    this.handleVisibility = () => {
      if (document.hidden) {
        this.stop();
      } else {
        this.tick();
        this.start();
      }
    };

    this.tick();
    this.start();
    document.addEventListener('visibilitychange', this.handleVisibility);
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.handleVisibility);
  }

  /* Reuse this visit's deadline if one is already running, so navigating
     between pages continues the countdown instead of restarting it.
     sessionStorage can throw in private modes or with storage blocked, so
     every access is guarded and falls back to a fresh in-memory deadline. */
  resolveDuration() {
    const minutes = parseFloat(this.dataset.duration) || 0;
    if (minutes <= 0) return NaN;

    const fresh = Date.now() + minutes * 60000;

    let stored = null;
    try {
      stored = parseInt(window.sessionStorage.getItem(STORE_KEY) || '', 10);
    } catch (error) {
      return fresh;
    }

    // Ignore a stored value that is already spent, or one further out than the
    // configured duration (which means the setting was lowered since).
    if (stored && stored > Date.now() && stored <= fresh) return stored;

    try {
      window.sessionStorage.setItem(STORE_KEY, String(fresh));
    } catch (error) {
      /* not fatal — the timer just restarts on the next page */
    }
    return fresh;
  }

  start() {
    this.stop();
    this.interval = setInterval(() => this.tick(), 1000);
  }

  stop() {
    if (this.interval) clearInterval(this.interval);
    this.interval = null;
  }

  /* Roll a fixed end date forward when the merchant has asked for a repeating
     cycle, so a weekly promotion does not need editing every week. */
  target(now) {
    if (this.mode === 'duration') return this.endAt;
    if (this.restartMs <= 0 || now < this.endAt) return this.endAt;
    const cycles = Math.ceil((now - this.endAt) / this.restartMs);
    return this.endAt + cycles * this.restartMs;
  }

  tick() {
    const now = Date.now();
    const remaining = this.target(now) - now;

    if (remaining <= 0) {
      this.expire();
      return;
    }

    const total = Math.floor(remaining / 1000);
    const d = Math.floor(total / 86400);
    const h = Math.floor((total % 86400) / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;

    // With no days field on screen, fold the days into the hours so the clock
    // still totals the real remaining time.
    this.paint('days', d);
    this.paint('hours', this.fields.days ? h : d * 24 + h);
    this.paint('minutes', m);
    this.paint('seconds', s);

    this.announce(d, h, m);
  }

  paint(key, value) {
    const node = this.fields[key];
    if (!node) return;

    const next = String(value).padStart(2, '0');
    if (node.textContent === next) return;

    node.textContent = next;

    if (REDUCED.matches) return;
    node.classList.add('is-tick');
    setTimeout(() => node.classList.remove('is-tick'), 200);
  }

  announce(d, h, m) {
    if (!this.live || m === this.lastMinuteAnnounced) return;
    this.lastMinuteAnnounced = m;

    const bits = [];
    if (d) bits.push(d + (d === 1 ? ' day' : ' days'));
    if (h) bits.push(h + (h === 1 ? ' hour' : ' hours'));
    bits.push(m + (m === 1 ? ' minute' : ' minutes'));
    this.live.textContent = bits.join(', ') + ' left';
  }

  expire() {
    this.stop();

    if (this.onExpiry === 'hide') {
      this.remove();
      return;
    }

    const clock = this.querySelector('[data-ct-clock]');
    const done = this.querySelector('[data-ct-done]');
    if (clock) clock.hidden = true;
    if (done) done.hidden = false;
    if (this.live) this.live.textContent = done ? done.textContent : '';
  }
}

if (!customElements.get('brand-countdown')) {
  customElements.define('brand-countdown', BrandCountdown);
}
